import { FunctionComponent, useState, useRef, useEffect } from "react";
import { UseFormRegister } from "react-hook-form";


interface IMultiSelectOption {
    id: string;
    label: string;
}


interface IMultiSelectDropdownProps {
    label: string;
    name: string;
    options: IMultiSelectOption[];
    register: UseFormRegister<any>;
    error?: string;
    containerStyles?: string;
    required?: boolean;
    placeholder?: string;
}


const MultiSelectDropdown: FunctionComponent<IMultiSelectDropdownProps> = ({
    label,
    name,
    options,
    register,
    error,
    containerStyles = '',
    required = false,
    placeholder = "Select options..."
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const dropdownRef = useRef<HTMLDivElement>(null);


    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };


        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    const handleOptionToggle = (optionId: string) => {
        const newSelectedValues = selectedValues.includes(optionId)
            ? selectedValues.filter(id => id !== optionId)
            : [...selectedValues, optionId];
        
        setSelectedValues(newSelectedValues);
    };


    const getDisplayText = () => {
        if (selectedValues.length === 0) return placeholder;
        if (selectedValues.length === 1) {
            const option = options.find(opt => opt.id === selectedValues[0]);
            return option?.label || '';
        }
        return `${selectedValues.length} options selected`;
    };


    const getSelectedLabels = () => {
        return selectedValues
            .map(id => options.find(opt => opt.id === id)?.label)
            .filter(Boolean)
            .join(', ');
    };


    return (
        <div className={`mb-6 ${containerStyles}`} ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {label} {required && <span className="text-red-500">*</span>}
            </label>


            {/* Hidden inputs for react-hook-form */}
            {selectedValues.map((value) => (
                <input
                    key={value}
                    type="hidden"
                    value={value}
                    {...register(name)}
                />
            ))}


            <div className="relative">
                {/* Dropdown Button */}
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className={`
                        w-full px-3 py-2 text-left border rounded-md shadow-sm bg-white
                        focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                        ${error 
                            ? 'border-red-300 text-red-900' 
                            : 'border-gray-300'
                        }
                        ${isOpen ? 'ring-2 ring-primary border-primary' : ''}
                    `}
                >
                    <div className="flex items-center justify-between">
                        <span className={selectedValues.length === 0 ? 'text-gray-400' : 'text-gray-900'}>
                            {getDisplayText()}
                        </span>
                        <svg
                            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </button>


                {/* Selected items preview */}
                {selectedValues.length > 1 && (
                    <div className="mt-1 text-xs text-gray-600 truncate">
                        {getSelectedLabels()}
                    </div>
                )}


                {/* Dropdown Panel */}
                {isOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {options.length === 0 ? (
                            <div className="px-3 py-2 text-gray-500">No options available</div>
                        ) : (
                            <div className="py-1">
                                {options.map((option) => (
                                    <label
                                        key={option.id}
                                        className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedValues.includes(option.id)}
                                            onChange={() => handleOptionToggle(option.id)}
                                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded mr-3"
                                        />
                                        <span className="text-sm text-gray-700">{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>


            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
        </div>
    );
};


export default MultiSelectDropdown;


